import { Injectable } from '@angular/core';
import { CourierScheduleService } from '@core/services/courier-shedule.service';
import {
  State, Selector, Action, StateContext,
} from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import {
  CourierScheduleData,
  CourierScheduleResponse,
  CourierWorkSchedulesFilter,
} from '@core/interfaces/courier-shedule.interface';
import { getGroupedSchedules, getUniqueDates, prepareScheduleMatrix } from '@core/helper/courier-work-schedule-helper';
import { Courier } from '@core/interfaces/courier-directory.interface';
import { RouteSheetsStateModel } from '@core/store/state/route-sheets.state';
import { DateRange, NgbDateRange } from '@core/interfaces/date-filter.interface';
import { getDefaultDateFilterState } from '@core/helper/state-helper';
import { gateDateRangeFromNgbDateRange } from '@core/helper/date-helper';
import { MutationResult } from 'apollo-angular';
import {
  ArchiveCourierSchedules,
  GetCourierSchedules,
  ResetCourierSchedules,
  SetSchedulesDateFilter,
  SetSchedulesFilter,
  UpdateCourierSchedule,
} from '../action/courier-work-schedule.action';

export class CourierSchedulesStateModel {
  schedules!: Array<Courier & { workSchedules: CourierScheduleResponse[] }>;

  groupedSchedules!: CourierScheduleData[];

  uniqueDates!: any[];

  scheduleMatrix!: any[];

  filter!: CourierWorkSchedulesFilter;

  rawDateFilter!: NgbDateRange;
}

@State<CourierSchedulesStateModel>({
  name: 'CourierSchedulesState',
  defaults: {
    schedules: [],
    groupedSchedules: [],
    uniqueDates: [],
    scheduleMatrix: [],
    filter: {},
    rawDateFilter: getDefaultDateFilterState(),
  },
})
@Injectable()
export class CourierSchedulesState {
  constructor(private courierScheduleService: CourierScheduleService) {}

  @Selector()
  static schedules(state: CourierSchedulesStateModel): Array<Courier & { workSchedules: CourierScheduleResponse[] }> {
    return state.schedules;
  }

  @Selector()
  static groupedSchedules(state: CourierSchedulesStateModel): CourierScheduleData[] {
    return state.groupedSchedules;
  }

  @Selector()
  static getUniqueDates(state: CourierSchedulesStateModel): string[] {
    return state.uniqueDates;
  }

  @Selector()
  static getScheduleMatrix(state: CourierSchedulesStateModel): CourierScheduleResponse[][][] {
    return state.scheduleMatrix;
  }

  @Selector()
  static filter(state: CourierSchedulesStateModel): CourierWorkSchedulesFilter {
    return state.filter;
  }

  @Selector()
  static getRawDateFilter(state: CourierSchedulesStateModel): NgbDateRange {
    return state.rawDateFilter;
  }

  @Selector()
  static getDateFilter(state: CourierSchedulesStateModel): DateRange {
    return gateDateRangeFromNgbDateRange(state.rawDateFilter);
  }

  @Action(GetCourierSchedules)
  public getCourierSchedules(
    ctx: StateContext<CourierSchedulesStateModel>,
    { courierWorkSchedulesFilter }: GetCourierSchedules,
  ): Observable<
    MutationResult<{ getListCourierWorkSchedule: Array<Courier & { workSchedules: CourierScheduleResponse[] }> }>
  > {
    return this.courierScheduleService.getCourierSchedule(courierWorkSchedulesFilter).pipe(
      tap((res) => {
        const schedules = res.data!.getListCourierWorkSchedule;
        const groupedSchedules = getGroupedSchedules(schedules);
        const uniqueDates = getUniqueDates(groupedSchedules);
        const scheduleMatrix = prepareScheduleMatrix(groupedSchedules, uniqueDates);

        ctx.patchState({
          schedules,
          groupedSchedules,
          uniqueDates,
          scheduleMatrix,
        });
      }),
    );
  }

  @Action(SetSchedulesFilter)
  public setFilter(ctx: StateContext<CourierSchedulesStateModel>, { filter }: SetSchedulesFilter): void {
    const filterToUse = {
      ...ctx.getState().filter,
      ...filter,
    };
    ctx.patchState({
      filter: filterToUse,
    });
  }

  @Action(ResetCourierSchedules)
  public resetCourierSchedules(ctx: StateContext<CourierSchedulesStateModel>): void {
    ctx.patchState({
      schedules: [],
      groupedSchedules: [],
      uniqueDates: [],
      scheduleMatrix: [],
      filter: {},
    });
  }

  @Action(SetSchedulesDateFilter)
  public SetRouteSheetsDateFilter(
    ctx: StateContext<RouteSheetsStateModel>,
    { dateFilter }: SetSchedulesDateFilter,
  ): void {
    ctx.patchState({
      rawDateFilter: dateFilter,
    });
  }
}
